import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Card,
  CardBody,
  Badge,
  Container,
  HStack,
  Divider,
  Spinner,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Html5Qrcode } from 'html5-qrcode';
import { apiService } from '../services/api';

const QRScannerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastCheckedIn, setLastCheckedIn] = useState<{
    name: string;
    organization?: string;
    vipStatus: string;
    time: string;
  } | null>(null);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef({ code: '', time: 0 });
  const mountedRef = useRef(true);

  const checkinMutation = useMutation({
    mutationFn: (token: string) => apiService.checkInByQR(token),
    onSuccess: (data) => {
      if (!mountedRef.current) return;

      queryClient.invalidateQueries({ queryKey: ['checkin-list', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });

      if (data.already_checked_in) {
        toast({
          title: '⚠️ Déjà enregistré',
          description: `${data.contact_name} est déjà enregistré`,
          status: 'warning',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      } else {
        toast({
          title: '✅ Check-in réussi',
          description: data.message,
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });

        setLastCheckedIn({
          name: data.contact_name,
          organization: data.organization,
          vipStatus: data.vip_status,
          time: new Date().toLocaleTimeString('fr-FR'),
        });

        setTimeout(() => {
          if (mountedRef.current) {
            setLastCheckedIn(null);
          }
        }, 5000);
      }

      setProcessing(false);
    },
    onError: (error: any) => {
      if (!mountedRef.current) return;

      toast({
        title: '❌ Erreur',
        description: error.message || 'QR code invalide',
        status: 'error',
        duration: 4000,
        position: 'top',
        isClosable: true,
      });

      setProcessing(false);
    },
  });

  const handleScan = (decodedText: string) => {
    // Prevent processing if already processing
    if (processing) {
      return;
    }

    // Validate token format
    if (!decodedText || decodedText.length < 10) {
      return;
    }

    // Debounce: ignore same code within 5 seconds
    const now = Date.now();
    if (
      decodedText === lastScanRef.current.code &&
      now - lastScanRef.current.time < 5000
    ) {
      return;
    }

    // Update last scan
    lastScanRef.current = { code: decodedText, time: now };

    // Set processing state
    setProcessing(true);

    // Process check-in
    checkinMutation.mutate(decodedText);
  };

  const startScanner = async () => {
    try {
      console.log('🎥 Starting scanner...');

      // Element should exist now because scanning=true renders it
      const element = document.getElementById('qr-reader-container');
      if (!element) {
        throw new Error('Conteneur de scanner non trouvé');
      }

      console.log('✅ Container element found');

      // Clean up any existing scanner
      if (scannerRef.current) {
        console.log('🧹 Cleaning up existing scanner');
        try {
          await scannerRef.current.stop();
        } catch (e) {
          // Ignore
        }
        scannerRef.current = null;
      }

      // Check cameras
      const devices = await Html5Qrcode.getCameras();
      console.log('📷 Available cameras:', devices);
      
      if (!devices || devices.length === 0) {
        throw new Error('Aucune caméra détectée');
      }

      // Create scanner
      const scanner = new Html5Qrcode('qr-reader-container');
      scannerRef.current = scanner;

      console.log('🚀 Starting camera...');

      // Start with back camera
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 5,
          qrbox: { width: 250, height: 250 },
        },
        handleScan,
        () => {}
      );

      console.log('✅ Scanner started successfully');

      toast({
        title: '📷 Scanner activé',
        status: 'info',
        duration: 2000,
        position: 'top',
        isClosable: true,
      });
    } catch (err: any) {
      console.error('❌ Scanner error:', err);
      
      toast({
        title: 'Erreur',
        description: err.message || 'Impossible de démarrer le scanner',
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
      
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) {
        console.error('Scanner stop error:', e);
      }
      scannerRef.current = null;
    }

    setScanning(false);
    setProcessing(false);
    lastScanRef.current = { code: '', time: 0 };

    toast({
      title: '⏸️ Scanner arrêté',
      status: 'info',
      duration: 2000,
      position: 'top',
      isClosable: true,
    });
  };

  const handleToggleScanning = () => {
    if (scanning) {
      stopScanner();
    } else {
      // Set scanning=true to render container, then start scanner
      setScanning(true);
      setTimeout(() => {
        startScanner();
      }, 200); // Wait 200ms for React to render
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const getVIPBadgeColor = (status: string) => {
    switch (status) {
      case 'very_vip': return 'purple';
      case 'board_member': return 'red';
      case 'vip': return 'blue';
      case 'sponsor': return 'green';
      case 'media': return 'orange';
      default: return 'gray';
    }
  };

  const getVIPLabel = (status: string) => {
    switch (status) {
      case 'very_vip': return 'VIP+';
      case 'board_member': return 'Conseil';
      case 'vip': return 'VIP';
      case 'sponsor': return 'Sponsor';
      case 'media': return 'Média';
      case 'none': return '';
      default: return status;
    }
  };

  return (
    <Box minH="100vh" bg="gray.900" py={8}>
      <Container maxW="container.md">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading color="white" size="lg">Scanner QR Code</Heading>
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              colorScheme="whiteAlpha"
              onClick={() => navigate(`/events/${id}`)}
            >
              Retour
            </Button>
          </HStack>

          <Divider borderColor="gray.700" />

          <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
            <CardBody>
              <VStack spacing={6}>
                {scanning ? (
                  <Box position="relative" w="full">
                    <Box
                      id="qr-reader-container"
                      w="full"
                      minH="400px"
                      borderRadius="lg"
                      overflow="hidden"
                      bg="black"
                      sx={{
                        '& video': {
                          width: '100%',
                          borderRadius: '8px',
                        },
                        '& input, & button, & select': {
                          display: 'none !important',
                        },
                      }}
                    />
                    {processing && (
                      <Box
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="blackAlpha.700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="lg"
                      >
                        <VStack>
                          <Spinner size="xl" color="white" />
                          <Text color="white" fontSize="lg" fontWeight="bold">
                            Traitement...
                          </Text>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    w="full"
                    h="400px"
                    bg="black"
                    borderRadius="lg"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <VStack spacing={4}>
                      <Text color="gray.400" fontSize="lg">📷 Caméra inactive</Text>
                      <Text color="gray.500" fontSize="sm">
                        Appuyez sur "Démarrer"
                      </Text>
                    </VStack>
                  </Box>
                )}

                <Button
                  colorScheme={scanning ? 'red' : 'green'}
                  size="lg"
                  w="full"
                  onClick={handleToggleScanning}
                  isDisabled={processing}
                >
                  {scanning ? '⏸️ Arrêter' : '▶️ Démarrer'}
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {lastCheckedIn && (
            <Alert status="success" variant="solid" borderRadius="lg" bg="green.600">
              <AlertIcon boxSize="40px" />
              <VStack align="start" spacing={1} flex={1}>
                <AlertTitle fontSize="lg">✓ Check-in réussi !</AlertTitle>
                <AlertDescription>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" fontSize="xl">{lastCheckedIn.name}</Text>
                    {lastCheckedIn.organization && (
                      <Text fontSize="sm">{lastCheckedIn.organization}</Text>
                    )}
                    <HStack mt={1}>
                      {lastCheckedIn.vipStatus !== 'none' && (
                        <Badge colorScheme={getVIPBadgeColor(lastCheckedIn.vipStatus)}>
                          {getVIPLabel(lastCheckedIn.vipStatus)}
                        </Badge>
                      )}
                      <Text fontSize="xs">• {lastCheckedIn.time}</Text>
                    </HStack>
                  </VStack>
                </AlertDescription>
              </VStack>
            </Alert>
          )}

          <Card bg="blue.900" borderColor="blue.700" borderWidth="1px">
            <CardBody>
              <VStack align="start" spacing={3} color="white">
                <Text fontWeight="bold" fontSize="lg">📱 Instructions</Text>
                <VStack align="start" spacing={2} fontSize="sm">
                  <Text>1. Appuyez sur "Démarrer le scan"</Text>
                  <Text>2. Autorisez l'accès à la caméra</Text>
                  <Text>3. Présentez le QR code</Text>
                  <Text>4. Attendez la confirmation</Text>
                  <Text>5. Retirez le QR code</Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
            <CardBody>
              <HStack justify="space-between" color="white">
                <Text fontSize="sm">Statut:</Text>
                <Badge
                  colorScheme={processing ? 'yellow' : scanning ? 'green' : 'gray'}
                  fontSize="md"
                  px={3}
                  py={1}
                >
                  {processing ? '⏳ Traitement' : scanning ? '● Actif' : '○ Inactif'}
                </Badge>
              </HStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default QRScannerPage;